import { useState } from "react";
import {
  Form,
  Input,
  Button,
  Modal,
  Row,
  Col,
  Select,
  App,
  Avatar,
  Typography,
} from "antd";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { useUser } from "../../contexts/UserContext";

const { Option } = Select;
const { Title, Text } = Typography;

interface ProfileFormValues {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

interface PasswordFormValues {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const Profile = () => {
  const { message } = App.useApp();
  const { user } = useUser();
  const [form] = Form.useForm<ProfileFormValues>();
  const [passwordForm] = Form.useForm<PasswordFormValues>();
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const handleProfileFinish = (values: ProfileFormValues) => {
    setIsEditing(false);
    message.success("Profile updated successfully!");
  };

  const handlePasswordFinish = (values: PasswordFormValues) => {
    setIsPasswordModalVisible(false);
    passwordForm.resetFields();
    message.success("Password changed successfully!");
  };

  // const handleEdit = () => {
  //   setIsEditing(true);
  // };

  // const handleCancel = () => {
  //   setIsEditing(false);
  //   form.resetFields();
  // };

  if (!user) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <Text>Loading user information...</Text>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
        <Row
          align="middle"
          justify="space-between"
          className="profile-header-row"
          style={{ marginBottom: "24px" }}
        >
          <Col>
            <div className="profile-avatar-info" style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <Avatar
                size={64}
                icon={<UserOutlined />}
                style={{ backgroundColor: "#1890ff" }}
              />
              <div>
                <Title level={3} style={{ margin: 0 }}>
                  {user.firstName} {user.lastName}
                </Title>
                <Text type="secondary">{user.email}</Text>
                <br />
                <Text type="secondary">Role: {user.role}</Text>
              </div>
            </div>
          </Col>
          {/* <Col>
            {!isEditing ? (
              <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
                Edit Profile
              </Button>
            ) : (
              <Space>
                <Button type="primary" onClick={() => form.submit()}>
                  Save Changes
                </Button>
                <Button onClick={handleCancel}>Cancel</Button>
              </Space>
            )}
          </Col> */}
        </Row>

        <Form
          form={form}
          layout="vertical"
          initialValues={{
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            role: user.role,
          }}
          onFinish={handleProfileFinish}
        >
          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[
                  { required: true, message: "Please enter your first name" },
                ]}
              >
                <Input placeholder="Your First Name" disabled={!isEditing} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="lastName"
                label="Last Name"
                rules={[
                  { required: true, message: "Please enter your last name" },
                ]}
              >
                <Input placeholder="Your Last Name" disabled={!isEditing} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: "Please enter your email" },
                  { type: "email", message: "Please enter a valid email" },
                ]}
              >
                <Input placeholder="Your Email" disabled={!isEditing} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="role" label="Role">
                <Select placeholder="Select Role" disabled={!isEditing}>
                  <Option value="admin">Admin</Option>
                  <Option value="hr">HR</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <div style={{ marginTop: 24 }}>
          <Button
            icon={<LockOutlined />}
            onClick={() => setIsPasswordModalVisible(true)}
            disabled={!isEditing}
          >
            Change Password
          </Button>
        </div>

      <Modal
        title="Change Password"
        open={isPasswordModalVisible}
        onCancel={() => setIsPasswordModalVisible(false)}
        footer={null}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordFinish}
        >
          <Form.Item
            name="oldPassword"
            label="Current Password"
            rules={[
              { required: true, message: "Please enter your current password" },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: "Please enter a new password" },
              { min: 6, message: "Password must be at least 6 characters" },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirm New Password"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: "Please confirm your new password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("The two passwords do not match!")
                  );
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Change Password
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Profile;
